"use client";

import { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AnimatedCard } from "@/components/motion/AnimatedCard";
import { Button } from "@/components/ui/Button";
import { LINKS } from "@/content/quikit";
import { Check } from "@/components/ui/icons";
import { SectionIntro } from "@/components/sections/SectionIntro";
import { QSG_ROLES, type RoleKey } from "@/content/quiksigns";
import { cn } from "@/lib/utils";

const EASE = [0.23, 1, 0.32, 1] as const;

/** Founders, team leads and teams — one workspace, three framings. */
export function Roles() {
  const [active, setActive] = useState<RoleKey>("sales");
  const rail = useRef<HTMLDivElement>(null);
  const panel = QSG_ROLES.panels[active];

  /**
   * On phones the rail scrolls horizontally, so a tab can sit off-screen. Nudge the rail's
   * own scrollLeft — `scrollIntoView` would drag the page with it.
   */
  function select(key: RoleKey, button: HTMLButtonElement) {
    setActive(key);
    const el = rail.current;
    if (!el || el.scrollWidth <= el.clientWidth) return;
    const left = button.offsetLeft - (el.clientWidth - button.offsetWidth) / 2;
    el.scrollTo({ left: Math.max(0, left), behavior: "smooth" });
  }

  return (
    <section id="roles" aria-labelledby="qsg-roles-title" className="scroll-mt-28 pt-[clamp(110px,11vw,170px)]">
      <div className="wrap">
        <SectionIntro
          eyebrow={QSG_ROLES.eyebrow}
          title={QSG_ROLES.title}
          titleId="qsg-roles-title"
          titleClassName="max-w-[17em]"
          text={QSG_ROLES.text}
          textClassName="max-w-[62ch]"
        />

        <div
          ref={rail}
          role="tablist"
          aria-label={QSG_ROLES.eyebrow}
          className="mx-auto mt-12 flex w-fit max-w-full gap-1 overflow-x-auto rounded-full bg-panel p-1 no-scrollbar md:mt-14"
        >
          {QSG_ROLES.tabs.map((t) => {
            const on = t.key === active;
            return (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={on}
                aria-controls={`qsg-role-${t.key}`}
                onClick={(e) => select(t.key, e.currentTarget)}
                className="relative shrink-0 rounded-full px-4 py-2.5 text-[14px] font-medium transition-colors"
              >
                {on && (
                  <motion.span
                    layoutId="qsg-role-pill"
                    className="absolute inset-0 rounded-full bg-ink"
                    transition={{ type: "spring", duration: 0.45, bounce: 0.12 }}
                  />
                )}
                <span className={cn("relative transition-colors", on ? "text-white" : "text-ink/70 hover:text-ink")}>{t.label}</span>
              </button>
            );
          })}
        </div>

        <AnimatedCard y={60} className="mt-6 overflow-hidden rounded-[28px] bg-panel p-6 md:p-10">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={active}
              id={`qsg-role-${active}`}
              role="tabpanel"
              initial={{ opacity: 0, y: 14, filter: "blur(6px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -10, filter: "blur(6px)" }}
              transition={{ duration: 0.42, ease: EASE }}
              className="grid grid-cols-1 gap-8 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] md:gap-12"
            >
              <div className="flex flex-col">
                <p className="text-[12.5px] font-medium uppercase tracking-[0.1em] text-accent">{panel.eyebrow}</p>
                <h3 className="mt-4 max-w-[18ch] text-[clamp(1.6rem,2.6vw,2.3rem)] font-[450] leading-[1.15] tracking-[-0.035em]">
                  {panel.title}
                </h3>
                <p className="mt-4 max-w-[46ch] text-[14px] leading-[1.6] text-muted">{panel.text}</p>
                <div className="mt-auto pt-8">
                  {/* A "Start free…" panel is a sign-up, not a demo request. */}
                  <Button
                    variant="mint"
                    href={/^start free/i.test(panel.cta) ? LINKS.register : "#demo"}
                    arrow="inline"
                    data-intent={/^start free/i.test(panel.cta) ? undefined : "demo"}
                  >
                    {panel.cta}
                  </Button>
                </div>
              </div>
              <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {panel.items.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 rounded-[16px] bg-white p-4 text-[14px]">
                    <span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-accent-soft text-accent">
                      <Check className="size-3" strokeWidth={2.4} />
                    </span>
                    <span className="text-ink/80">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </AnimatePresence>
        </AnimatedCard>
      </div>
    </section>
  );
}
